import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Edit, Share2, Send, Check, Printer, Camera, ImageIcon, Trash2, UserCog, X, Home, Eye, Mail, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const brl = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const STATUS_CONFIG = {
  nao_enviado: { label: 'Não enviado', color: 'bg-zinc-700/50 text-zinc-300' },
  enviado: { label: 'Enviado', color: 'bg-blue-500/20 text-blue-400' },
  aprovado: { label: 'Aprovado', color: 'bg-green-500/20 text-green-400' },
  em_andamento: { label: 'Em andamento', color: 'bg-yellow-500/20 text-yellow-400' },
  concluido: { label: 'Concluído', color: 'bg-purple-500/20 text-purple-400' },
  pago: { label: 'Pago / Recibo', color: 'bg-emerald-500/20 text-emerald-400' },
};

const PAGAMENTO_LABELS = { pix: 'PIX', cartao_avista: 'Cartão à Vista', cartao_parcelado: 'Cartão Parcelado', boleto: 'Boleto' };

function compressImage(dataUrl, maxW = 900) {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(maxW / img.width, 1);
      const c = document.createElement('canvas');
      c.width = img.width * scale; c.height = img.height * scale;
      c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
      resolve(c.toDataURL('image/jpeg', 0.7));
    };
    img.src = dataUrl;
  });
}

export default function ProposalView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canCreate } = useAuth();
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [showAssign, setShowAssign] = useState(false);
  const [addingPhoto, setAddingPhoto] = useState(false);
  const [docScale, setDocScale] = useState(1);
  const photoRef = useRef(null);

  useEffect(() => {
    const update = () => setDocScale(Math.min((window.innerWidth - 16) / 794, 1));
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  useEffect(() => { loadProposal(); loadEmployees(); }, [id]);

  const loadProposal = async () => {
    try { const res = await api.get(`/proposals/${id}`); setProposal(res.data); }
    catch { toast.error('Orçamento não encontrado'); navigate('/'); }
    finally { setLoading(false); }
  };

  const loadEmployees = async () => {
    try { const res = await api.get('/employees/'); setEmployees(res.data); } catch {}
  };

  const changeStatus = async (newStatus) => {
    try {
      const res = await api.put(`/proposals/${id}`, { status: newStatus });
      setProposal(res.data);
      toast.success(`Status: ${STATUS_CONFIG[newStatus]?.label}`);
      if (newStatus === 'pago') toast.info('Orçamento convertido em recibo!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao alterar status'); }
  };

  const shareLink = () => {
    const url = `${window.location.origin}/p/${id}`;
    navigator.clipboard?.writeText(url);
    toast.success('Link copiado!');
  };

  const [sendingEmail, setSendingEmail] = useState(false);
  const sendEmail = async () => {
    if (!proposal?.cliente_dados?.email) { toast.error('Cliente sem e-mail cadastrado'); return; }
    setSendingEmail(true);
    try {
      const res = await api.post(`/proposals/${id}/send-email`);
      toast.success(res.data.message || 'Email enviado!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao enviar email');
    } finally { setSendingEmail(false); }
  };

  const duplicateProposal = async () => {
    if (!canCreate()) {
      toast.error('Assine um plano para duplicar orçamentos');
      navigate('/subscribe');
      return;
    }
    try {
      const res = await api.post(`/proposals/${id}/duplicate`);
      toast.success('Orçamento duplicado!');
      navigate(`/proposals/${res.data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao duplicar orçamento');
    }
  };

  const sendWhatsApp = () => {
    if (!proposal) return;
    const url = `${window.location.origin}/p/${id}`;
    const wpp = proposal.cliente_dados?.whatsapp?.replace(/\D/g, '') || '';
    const msg = `Olá ${proposal.cliente_nome || ''}! Segue o orçamento *${proposal.titulo}* no valor de *${brl(proposal.valor)}*: ${url}`;
    window.open(`https://wa.me/${wpp}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const assignEmployee = async () => {
    if (!selectedEmployee) { toast.error('Selecione um funcionário'); return; }
    try {
      const res = await api.post(`/employees/proposals/${id}/assign`, { employee_id: selectedEmployee });
      toast.success('Funcionário atribuído!');
      if (res.data.wpp_link) {
        window.open(res.data.wpp_link, '_blank');
      } else if (res.data.company_id) {
        const portalLink = `${window.location.origin}/portal/${res.data.company_id}`;
        const empNome = res.data.employee_nome || 'Funcionário';
        const emp = employees.find(e => e.id === selectedEmployee);
        const wpp = (emp?.whatsapp || '').replace(/\D/g, '');
        if (wpp) {
          const msg = `Olá ${empNome}! Você tem um serviço atribuído. Acesse: ${portalLink}`;
          window.open(`https://wa.me/${wpp}?text=${encodeURIComponent(msg)}`, '_blank');
        }
      }
      loadProposal();
      setShowAssign(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro ao atribuir'); }
  };

  const handleAddPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setAddingPhoto(true);
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        const compressed = await compressImage(ev.target.result);
        await api.post(`/proposals/${id}/photos`, { photo_base64: compressed });
        toast.success('Foto adicionada!');
        loadProposal();
        setAddingPhoto(false);
      };
      reader.readAsDataURL(file);
    } catch { toast.error('Erro ao adicionar foto'); setAddingPhoto(false); }
    e.target.value = '';
  };

  const removePhoto = async (idx) => {
    try {
      await api.delete(`/proposals/${id}/photos/${idx}`);
      toast.success('Foto removida');
      loadProposal();
    } catch { toast.error('Erro ao remover foto'); }
  };

  if (loading) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>;
  if (!proposal) return null;

  const cfg = STATUS_CONFIG[proposal.status] || STATUS_CONFIG.nao_enviado;
  const sortedItems = [...proposal.itens].sort((a, b) => a.position - b.position);
  const NEXT_STATUSES = {
    nao_enviado: [{ v: 'enviado', label: 'Marcar como Enviado', color: 'text-blue-400 border-blue-500/30 hover:bg-blue-500/10' }],
    enviado: [{ v: 'aprovado', label: 'Marcar Aprovado', color: 'text-green-400 border-green-500/30 hover:bg-green-500/10' }],
    aprovado: [{ v: 'em_andamento', label: 'Em andamento', color: 'text-yellow-400 border-yellow-500/30 hover:bg-yellow-500/10' }],
    em_andamento: [{ v: 'concluido', label: 'Marcar Concluído', color: 'text-purple-400 border-purple-500/30 hover:bg-purple-500/10' }],
    concluido: [{ v: 'pago', label: 'Marcar Pago (gerar recibo)', color: 'text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10' }],
    pago: [],
  };
  const nextActions = NEXT_STATUSES[proposal.status] || [];
  const empresa = proposal.empresa_dados || {};
  const fp = proposal.forma_pagamento;
  const showPhotos = ['aprovado', 'em_andamento', 'concluido', 'pago'].includes(proposal.status);

  return (
    <div className="animate-fade-in pb-10">
      {/* Print CSS */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; }
        }
      `}</style>

      {/* Barra de ações — dark theme, sistema interno */}
      <div className="max-w-[900px] mx-auto px-4 mb-4 no-print">
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => navigate('/')} data-testid="retornar-btn"
            className="flex items-center gap-1.5 text-sm text-zinc-400 hover:text-white border border-zinc-800 px-3 py-1.5 rounded-lg transition-colors">
            <Home size={14} />
            Retornar
          </button>
          <div className="flex-1" />
          {nextActions.map(a => (
            <button key={a.v} onClick={() => changeStatus(a.v)}
              className={`flex items-center gap-1.5 text-sm border px-3 py-1.5 rounded-lg transition-colors ${a.color}`}>
              <Check size={14} />
              {a.label}
            </button>
          ))}
          {employees.length > 0 && ['aprovado', 'em_andamento'].includes(proposal.status) && (
            <button onClick={() => setShowAssign(p => !p)}
              className="flex items-center gap-1.5 text-sm border border-zinc-700 text-zinc-300 hover:text-orange-400 hover:border-orange-500/30 px-3 py-1.5 rounded-lg transition-colors">
              <UserCog size={14} />
              {proposal.employee_nome ? `Funcionário: ${proposal.employee_nome}` : 'Atribuir funcionário'}
            </button>
          )}
          <button onClick={() => navigate(`/proposals/${id}/edit`)}
            className="flex items-center gap-1.5 text-sm border border-zinc-700 text-zinc-300 hover:text-white px-3 py-1.5 rounded-lg transition-colors">
            <Edit size={14} />
            Editar
          </button>
          <button onClick={duplicateProposal}
            className="flex items-center gap-1.5 text-sm border border-zinc-700 text-zinc-300 hover:text-white px-3 py-1.5 rounded-lg transition-colors">
            <Copy size={14} />
            Duplicar
          </button>
          <button onClick={shareLink}
            className="flex items-center gap-1.5 text-sm border border-zinc-700 text-zinc-300 hover:text-white px-3 py-1.5 rounded-lg transition-colors">
            <Share2 size={14} />
            Link
          </button>
          <button data-testid="send-email-btn" onClick={sendEmail} disabled={sendingEmail}
            className="flex items-center gap-1.5 text-sm border border-zinc-700 text-zinc-300 hover:text-blue-400 hover:border-blue-500/30 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
            <Mail size={14} />
            {sendingEmail ? 'Enviando...' : 'Email'}
          </button>
          <button data-testid="preview-proposal-btn" onClick={() => window.open(`/p/${id}`, '_blank')}
            className="flex items-center gap-1.5 text-sm border border-zinc-700 text-zinc-300 hover:text-orange-400 hover:border-orange-500/30 px-3 py-1.5 rounded-lg transition-colors">
            <Eye size={14} />
            Pré-visualizar
          </button>
          <button onClick={sendWhatsApp} data-testid="send-whatsapp-btn"
            className="flex items-center gap-1.5 text-sm bg-[#25D366]/20 text-[#25D366] border border-[#25D366]/30 hover:bg-[#25D366]/30 px-3 py-1.5 rounded-lg transition-colors">
            <Send size={14} />
            Enviar
          </button>
          <button onClick={() => window.print()} data-testid="print-btn"
            className="flex items-center gap-1.5 text-sm border border-zinc-700 text-zinc-300 hover:text-white px-3 py-1.5 rounded-lg transition-colors">
            <Printer size={14} />
            Imprimir
          </button>
        </div>
      </div>

      {/* Painel de atribuição de funcionário */}
      {showAssign && (
        <div className="max-w-[900px] mx-auto px-4 mb-4 no-print">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-medium text-white text-sm">Atribuir funcionário</h3>
              <button onClick={() => setShowAssign(false)} className="text-zinc-500 hover:text-white"><X size={16} /></button>
            </div>
            <div className="flex gap-2">
              <select className="field-select flex-1" value={selectedEmployee} onChange={e => setSelectedEmployee(e.target.value)}>
                <option value="">Selecione um funcionário...</option>
                {employees.filter(e => e.is_active).map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
              </select>
              <button onClick={assignEmployee} className="bg-orange-500 hover:bg-orange-400 text-white font-semibold px-4 py-2 rounded-xl text-sm transition-colors">
                Atribuir
              </button>
            </div>
            <p className="text-xs text-zinc-500 mt-2">O funcionário receberá o link via WhatsApp após atribuição.</p>
          </div>
        </div>
      )}

      {/* Wrapper A4 — zoom automático para encaixar na tela, pinch-zoom disponível */}
      <div style={{ width: '100%' }}>
        <div
          id="proposal-doc"
          style={{
            width: '794px',
            zoom: docScale,
            margin: '0 auto',
            background: '#ffffff',
            color: '#111111',
            padding: '40px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            borderRadius: '4px',
          }}
        >
          {/* Cabeçalho */}
          <div style={{ background: '#f9fafb', padding: '24px', borderBottom: '1px solid #e5e7eb', borderRadius: '4px 4px 0 0', margin: '-40px -40px 32px -40px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
              <div style={{ flex: 1 }}>
                {empresa.logo && <img src={empresa.logo} alt="Logo" style={{ height: '56px', marginBottom: '12px', objectFit: 'contain' }} />}
                <div style={{ fontWeight: 'bold', fontSize: '18px', color: '#111' }}>{empresa.nome || 'Empresa'}</div>
                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px', lineHeight: '1.7' }}>
                  {empresa.cnpj && <div>CNPJ: {empresa.cnpj}</div>}
                  {(empresa.end || empresa.cidade) && <div>{[empresa.end, empresa.num, empresa.cidade, empresa.uf].filter(Boolean).join(', ')}</div>}
                  {empresa.tel && <div>Tel: {empresa.tel}</div>}
                  {empresa.email && <div>{empresa.email}</div>}
                </div>
              </div>
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ display: 'inline-block', background: cfg.color.includes('green') ? '#dcfce7' : cfg.color.includes('blue') ? '#dbeafe' : cfg.color.includes('yellow') ? '#fef9c3' : '#f3f4f6', color: cfg.color.includes('green') ? '#15803d' : cfg.color.includes('blue') ? '#1d4ed8' : cfg.color.includes('yellow') ? '#854d0e' : '#374151', padding: '2px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, marginBottom: '8px' }}>
                  {cfg.label}
                </div>
                <div style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase' }}>{proposal.is_recibo ? 'RECIBO' : proposal.tipo?.toUpperCase()}</div>
                <div style={{ fontFamily: 'monospace', fontWeight: 'bold', color: '#f97316', fontSize: '20px' }}>#{proposal.is_recibo ? proposal.recibo_num : proposal.num}</div>
              </div>
            </div>
          </div>

          {/* Título + Cliente + Datas */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
            <div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '3px', letterSpacing: '.05em' }}>Título</div>
              <div style={{ fontWeight: '600', color: '#111', fontSize: '15px' }}>{proposal.titulo}</div>
            </div>
            <div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '3px', letterSpacing: '.05em' }}>Cliente</div>
              <div style={{ fontWeight: '600', color: '#111', fontSize: '15px' }}>{proposal.cliente_nome || '—'}</div>
              {proposal.cliente_dados?.cpf_cnpj && <div style={{ fontSize: '12px', color: '#6b7280' }}>CPF/CNPJ: {proposal.cliente_dados.cpf_cnpj}</div>}
            </div>
            <div>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '3px', letterSpacing: '.05em' }}>Emissão</div>
              <div style={{ fontSize: '14px', color: '#374151' }}>{proposal.emissao || '—'}</div>
            </div>
            {proposal.validade && (
              <div>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '3px', letterSpacing: '.05em' }}>Validade</div>
                <div style={{ fontSize: '14px', color: '#374151' }}>{proposal.validade}</div>
              </div>
            )}
            {proposal.employee_nome && (
              <div>
                <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '3px', letterSpacing: '.05em' }}>Responsável</div>
                <div style={{ fontSize: '14px', color: '#374151' }}>{proposal.employee_nome}</div>
              </div>
            )}
          </div>

          {/* Tabela de itens */}
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #d1d5db' }}>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '44px' }}>Item</th>
                <th style={{ textAlign: 'left', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600 }}>Descrição</th>
                <th style={{ textAlign: 'right', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '50px' }}>Qtd</th>
                <th style={{ textAlign: 'left', padding: '8px 4px 8px 8px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '40px' }}>Un</th>
                <th style={{ textAlign: 'right', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '100px' }}>Unit.</th>
                <th style={{ textAlign: 'right', padding: '8px 4px', fontSize: '11px', color: '#6b7280', fontWeight: 600, width: '100px' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map(item => (
                <tr key={item.position} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px 4px', fontFamily: 'monospace', fontSize: '11px', color: '#9ca3af' }}>{String(item.position).padStart(3, '0')}</td>
                  <td style={{ padding: '10px 4px', color: '#111', fontWeight: 500 }}>{item.descricao}</td>
                  <td style={{ padding: '10px 4px', textAlign: 'right', color: '#374151' }}>{item.quantidade}</td>
                  <td style={{ padding: '10px 4px 10px 8px', fontSize: '11px', color: '#6b7280' }}>{item.unidade}</td>
                  <td style={{ padding: '10px 4px', textAlign: 'right', fontFamily: 'monospace', color: '#374151' }}>{brl(item.valor_unitario)}</td>
                  <td style={{ padding: '10px 4px', textAlign: 'right', fontFamily: 'monospace', color: '#111', fontWeight: 600 }}>{brl(item.valor_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totais */}
          <div style={{ borderTop: '2px solid #111', paddingTop: '12px', marginBottom: '24px' }}>
            {proposal.desconto > 0 && (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b7280', padding: '3px 0' }}>
                  <span>Subtotal</span><span style={{ fontFamily: 'monospace' }}>{brl(sortedItems.reduce((s, i) => s + i.valor_total, 0))}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#6b7280', padding: '3px 0' }}>
                  <span>Desconto</span><span style={{ fontFamily: 'monospace', color: '#dc2626' }}>- {brl(proposal.desconto)}</span>
                </div>
              </>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '16px', color: '#111', paddingTop: '8px', borderTop: '1px solid #e5e7eb', marginTop: '8px' }}>
              <span>TOTAL</span>
              <span style={{ fontFamily: 'monospace', color: '#f97316' }}>{brl(proposal.valor)}</span>
            </div>
          </div>

          {/* Forma de pagamento */}
          {fp && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '4px', letterSpacing: '.05em' }}>Forma de Pagamento</div>
              <div style={{ fontWeight: 600, fontSize: '14px', color: '#111' }}>{PAGAMENTO_LABELS[fp.tipo] || fp.tipo}</div>
              {fp.tipo === 'cartao_parcelado' && fp.parcelas && (
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>{fp.parcelas}x de <span style={{ color: '#f97316', fontFamily: 'monospace' }}>{brl(fp.valor_parcela)}</span> = Total: <span style={{ fontFamily: 'monospace' }}>{brl(proposal.valor)}</span></div>
              )}
              {fp.tipo === 'boleto' && fp.vencimento_boleto && (
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '4px' }}>Vencimento: {fp.vencimento_boleto}</div>
              )}
            </div>
          )}

          {/* Observações */}
          {proposal.obs && (
            <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px' }}>
              <div style={{ fontSize: '10px', textTransform: 'uppercase', color: '#9ca3af', marginBottom: '4px', letterSpacing: '.05em' }}>Observações</div>
              <p style={{ fontSize: '13px', color: '#374151', whiteSpace: 'pre-line' }}>{proposal.obs}</p>
            </div>
          )}

          {/* Assinaturas */}
          {(proposal.assinatura_img || empresa.assinatura_empresa) && (
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '24px', textAlign: 'center' }}>
              {empresa.assinatura_empresa && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>ASSINATURA DO PRESTADOR</div>
                  <img src={empresa.assinatura_empresa} alt="Assinatura empresa" style={{ maxHeight: '64px', margin: '0 auto', display: 'block', borderBottom: '1px solid #9ca3af', paddingBottom: '4px', paddingLeft: '16px', paddingRight: '16px' }} />
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>{empresa.nome}</div>
                  {empresa.cnpj && <div style={{ fontSize: '11px', color: '#9ca3af' }}>CNPJ: {empresa.cnpj}</div>}
                </div>
              )}
              {proposal.assinatura_img && (
                <>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px' }}>ASSINATURA DO CLIENTE</div>
                  <img src={proposal.assinatura_img} alt="Assinatura cliente" style={{ maxHeight: '80px', margin: '0 auto', display: 'block', borderBottom: '1px solid #9ca3af', paddingBottom: '8px', paddingLeft: '16px', paddingRight: '16px' }} />
                  <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '8px' }}>{proposal.cliente_nome || 'Cliente'}</div>
                  {proposal.assinatura_data && <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{new Date(proposal.assinatura_data).toLocaleDateString('pt-BR')}</div>}
                </>
              )}
            </div>
          )}

          {/* Cupons fiscais */}
          {proposal.cupons_anexos?.length > 0 && (
            <div style={{ marginTop: '24px' }} className="no-print">
              <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                <Camera size={12} /> CUPONS FISCAIS ({proposal.cupons_anexos.length})
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {proposal.cupons_anexos.map(c => (
                  <img key={c.id} src={c.imagem_base64} alt="Cupom" style={{ height: '80px', width: 'auto', borderRadius: '8px', border: '1px solid #e5e7eb', objectFit: 'cover' }} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Fotos da execução — fora do A4 */}
      {showPhotos && (
        <div className="max-w-[900px] mx-auto px-4 mt-4 no-print">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-heading font-semibold text-white flex items-center gap-2">
                <ImageIcon size={18} className="text-orange-400" />
                Fotos da Execução
                <span className="text-xs text-zinc-500 font-normal">({(proposal.execution_photos || []).length})</span>
              </h2>
              <label className={`flex items-center gap-1.5 text-sm border border-orange-500/30 bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 px-3 py-1.5 rounded-lg cursor-pointer transition-colors ${addingPhoto ? 'opacity-50' : ''}`}>
                <Camera size={14} />
                {addingPhoto ? 'Enviando...' : '+ Adicionar foto'}
                <input ref={photoRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleAddPhoto} disabled={addingPhoto} />
              </label>
            </div>
            {(proposal.execution_photos || []).length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {proposal.execution_photos.map((photo, idx) => (
                  <div key={idx} className="relative group aspect-square rounded-xl overflow-hidden bg-zinc-800 border border-zinc-700">
                    <img src={photo} alt={`Foto ${idx + 1}`} className="w-full h-full object-cover" />
                    <button onClick={() => removePhoto(idx)}
                      className="absolute top-1.5 right-1.5 w-6 h-6 bg-red-500 hover:bg-red-400 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Trash2 size={11} className="text-white" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-zinc-800 rounded-xl">
                <ImageIcon size={28} className="text-zinc-700 mx-auto mb-2" />
                <p className="text-zinc-600 text-sm">Nenhuma foto adicionada</p>
                <p className="text-zinc-700 text-xs mt-1">Registre fotos das peças, materiais e serviços realizados</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
